import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Colors } from "../../../../utils/colors";
import { Icon } from "@iconify/react";
import {
  DMAutomation,
  DMAutomationPlatform,
  DMAutomationState,
  DMAutomationTrigger,
  DMAutomationTriggerContentType,
  InstagramPost,
  InstagramPostsPaging,
  IntegrationConnectionStatus,
  KeywordRuleMode,
  NotificationType,
  OnboardingFlowType,
} from "@markit/common.types";
import SelectableOptionCard from "../../../Containers/Items/SelectableOptionCard";
import CustomTextField from "../../../CustomTextField";
import Skeleton from "react-loading-skeleton";
import TextHoverButton from "../../../Buttons/TextHoverButton";
import {
  getDMAutomationKeywordIncludeTerms,
  getNumDMAutomationTriggers,
} from "../../../../utils/dmAutomationUtils";
import { DMAutomationConfirmDeleteModal } from "../Items/DMAutomationConfirmDeleteModal";
import RectangleButton from "../../../Buttons/RectangleButton";
import {
  censoredWordsDetected,
  detectedCensored,
  MARKIT_DOCS,
  selectRandomDMAutomationPublicReply,
  isReplyDmTrigger,
  isReplyStoryTrigger,
  GetDateWithTime,
} from "@markit/common.utils";
import { HorizontalDivider } from "../../../Dividers/HorizontalDivider";
import { DropdownMenu, DropdownMenuItem } from "../../../DropdownMenu";
import DropdownFilter from "../../../DropdownsAndTabs/DropdownFilter";
import { BackButton } from "../../../Buttons/BackButton";
import {
  getInstagramNextTriggerAccountIds,
  fetchInstagramPostsPage,
  fetchInstagramStoriesPage,
} from "../../../../utils/instagramApiUtils";
import {
  fetchFacebookPostsPage,
  facebookPageAsProfilePicAccount,
} from "../../../../utils/facebookApiUtils";
import ConnectInstagramModal from "../Instagram/ConnectInstagramModal";
import ConnectFacebookModal from "../Facebook/ConnectFacebookModal";
import { API } from "../../../../API";
import { showNotificationBanner } from "../../../../utils/notificationUtils";
import { useDispatch, useSelector } from "react-redux";
import PopupModalContainer, {
  PopupModalContainerWidth,
} from "../../../Containers/PopupModalContainer";
import { CircularProgress } from "@mui/material";
import { getAccountSlice } from "../../../../redux/slices/accountSlice";
import AlertContainer from "../../../Containers/AlertContainer";
import isEqual from "lodash.isequal";
import ConfirmDiscardChangesModal from "../../../Containers/ConfirmPopups/ConfirmDiscardChangesModal";
import OnboardingTourStep from "../../../Onboarding/OnboardingTourStep";
import { getInstagramDmTourTotalSteps } from "../../../../utils/onboardingFlowUtils";
import SocialProfilePic from "../../../DisplayItem/AutoDM/SocialProfilePic";
import StandardBorderedContainer from "../../../Containers/StandardBorderedContainer";
import {
  hasSocialAccountConnection,
  isAccountConnected,
} from "../../../../utils/socialAccountUtils";

type DMAutomationCreateTriggerProps = {
  triggerItem: DMAutomationTrigger;
  setTriggerItem: (trigger: DMAutomationTrigger) => void;
  dmAutomation: DMAutomation;
  selectTriggerItem: (trigger: DMAutomationTrigger | undefined) => void;
  deleteTriggerOnPress: () => void;
  nextOnPress?: () => void;
  onPostCaptionChange?: (caption: string | undefined) => void;
};

// Panel that lays out the steps to create/edit a trigger
const DMAutomationCreateTrigger = (props: DMAutomationCreateTriggerProps) => {
  const {
    triggerItem,
    setTriggerItem,
    dmAutomation,
    selectTriggerItem,
    deleteTriggerOnPress,
    nextOnPress,
    onPostCaptionChange,
  } = props;
  const dispatch = useDispatch();
  const { accountData, instagramAccounts, facebookPages } =
    useSelector(getAccountSlice);
  const [loadingPosts, setLoadingPosts] = useState(true);
  const [postsFetchError, setPostsFetchError] = useState(false);
  const [showReconnectModal, setShowReconnectModal] = useState(false);
  const [triggerTourStep, setTriggerTourStep] = useState(1);
  const [savingTrigger, setSavingTrigger] = useState(false);
  const [instagramPosts, setInstagramPosts] = useState<InstagramPost[]>([]);
  const [instagramPostsPaging, setInstagramPostsPaging] =
    useState<InstagramPostsPaging>({
      nextCursor: null,
      hasMore: false,
    });
  const [loadingMorePosts, setLoadingMorePosts] = useState(false);
  const [showAllPostsModal, setShowAllPostsModal] = useState(false);
  const [showSelectPostError, setShowSelectPostError] = useState(false);
  const [showKeywordError, setShowKeywordError] = useState(false);
  const [showPublicRepliesError, setShowPublicRepliesError] = useState(false);
  const [alertText, setAlertText] = useState<{
    header: string;
    subHeader: string;
  }>({ header: "", subHeader: "" });
  // Local state for the trigger item keywords
  const [hasKeywordRules, setHasKeywordRules] = useState(false);
  // Local state for the trigger item public replies
  const [hasPublicReplies, setHasPublicReplies] = useState(false);
  const [showDiscardChangesModal, setShowDiscardChangesModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // For tracking local changes made
  const initialTriggerRef = useRef(triggerItem);
  const initialHasKeywordRulesRef = useRef(hasKeywordRules);
  const initialHasPublicRepliesRef = useRef(hasPublicReplies);

  const INITIAL_WINDOW_SIZE = 4;
  const MORE_POSTS_SIZE = 12;
  const IMAGE_WIDTH = 72;
  const IMAGE_HEIGHT = 72;

  const styles = {
    headerRow: {
      flexShrink: 0,
      height: 58,
      paddingInline: 14,
    },
    stepContainer: {
      borderRadius: 12,
      border: `0.5px solid ${Colors.GRAY2}`,
      backgroundColor: Colors.WHITE1,
      padding: 14,
      gap: 10,
    },
    anyKeywordNoticeContainer: {
      borderRadius: 12,
      padding: 14,
      backgroundColor: "rgba(255, 204, 0, 0.2)",
      border: `1px solid ${Colors.YELLOW1}`,
    },
  };

  const isFacebook = useMemo(
    () => dmAutomation.platform === DMAutomationPlatform.FACEBOOK,
    [dmAutomation.platform]
  );
  // Reply to DM / Reply to story triggers (MARK-3878) have their own, shorter flows
  const isReplyStory = useMemo(
    () => isReplyStoryTrigger(triggerItem),
    [triggerItem]
  );
  const isReplyDm = useMemo(() => isReplyDmTrigger(triggerItem), [triggerItem]);
  const isReply = isReplyStory || isReplyDm;
  const totalSteps = isReplyDm ? 1 : isReplyStory ? 2 : 3;
  const tourTotalSteps = getInstagramDmTourTotalSteps(triggerItem);
  const contentLabel = useMemo(
    () =>
      isReplyStory
        ? "Story"
        : isReplyDm
          ? "DM"
          : isFacebook
            ? "Post"
            : "Post or Reel",
    [isFacebook, isReplyStory, isReplyDm]
  );
  const contentLabelPlural = useMemo(
    () => (isReplyStory ? "Stories" : isFacebook ? "Posts" : "Posts or Reels"),
    [isFacebook, isReplyStory]
  );

  const isDraftAutomation = useMemo(
    () => dmAutomation.state === DMAutomationState.DRAFT,
    [dmAutomation.state]
  );
  const isEditing = useMemo(() => triggerItem.id !== "", [triggerItem.id]);

  const madeChanges = useMemo(
    () =>
      !isEqual(triggerItem, initialTriggerRef.current) ||
      initialHasKeywordRulesRef.current !== hasKeywordRules ||
      initialHasPublicRepliesRef.current !== hasPublicReplies,
    [triggerItem, hasKeywordRules, hasPublicReplies]
  );

  const publicReplies = useMemo(() => {
    return triggerItem?.publicReplies ?? [];
  }, [triggerItem]);

  const keywordRules = useMemo(() => {
    return triggerItem?.keywordRules ?? [];
  }, [triggerItem]);

  const automationKeywordRules = useMemo(
    () => getDMAutomationKeywordIncludeTerms(keywordRules),
    [keywordRules]
  );

  // Any contentId that isn't one of the "all/next/any" sentinels is a specific post/reel/story id
  const isSpecificPostTrigger = useMemo(
    () =>
      triggerItem.contentId !== DMAutomationTriggerContentType.ALL &&
      triggerItem.contentId !== DMAutomationTriggerContentType.NEXT &&
      triggerItem.contentId !== DMAutomationTriggerContentType.ALL_STORY &&
      triggerItem.contentId !== DMAutomationTriggerContentType.NEXT_STORY &&
      triggerItem.contentId !== DMAutomationTriggerContentType.ALL_DM,
    [triggerItem.contentId]
  );

  const connectedAccounts = useMemo(
    () =>
      isFacebook
        ? facebookPages
            .filter((page) => isAccountConnected(page.status))
            .map(facebookPageAsProfilePicAccount)
        : instagramAccounts.filter((account) =>
            isAccountConnected(account.status)
          ),
    [isFacebook, facebookPages, instagramAccounts]
  );

  // Falls back to the first connected account until the trigger has one set
  const selectedAccountId = useMemo(
    () => triggerItem.igAccountId || connectedAccounts[0]?.id || "",
    [triggerItem.igAccountId, connectedAccounts]
  );

  const selectedAccount = useMemo(
    () => connectedAccounts.find((account) => account.id === selectedAccountId),
    [connectedAccounts, selectedAccountId]
  );

  const selectedAccountErrored = useMemo(
    () => selectedAccount?.status === IntegrationConnectionStatus.ERROR,
    [selectedAccount]
  );

  const updateTriggerItemSettings = useCallback(
    (trigger: Partial<DMAutomationTrigger>) =>
      setTriggerItem({ ...triggerItem, ...trigger } as DMAutomationTrigger),
    [setTriggerItem, triggerItem]
  );

  useEffect(() => {
    // Pre-set the keywords and public replies local states
    if (keywordRules.length > 0) {
      setHasKeywordRules(true);
      initialHasKeywordRulesRef.current = true;
    }
    if (publicReplies.length > 0) {
      setHasPublicReplies(true);
      initialHasPublicRepliesRef.current = true;
    }
  }, [keywordRules.length, publicReplies.length]);

  // Persist the resolved default account onto the trigger once one is available
  useEffect(() => {
    if (!triggerItem.igAccountId && selectedAccountId) {
      updateTriggerItemSettings({ igAccountId: selectedAccountId });
    }
  }, [triggerItem.igAccountId, selectedAccountId, updateTriggerItemSettings]);

  // Reply to DM always has keywords, so start it with one empty keyword field
  useEffect(() => {
    if (
      isReplyDm &&
      keywordRules.length === 0 &&
      (triggerItem.igAccountId || !selectedAccountId)
    ) {
      const initialRules = [{ mode: KeywordRuleMode.INCLUDE, terms: [""] }];
      updateTriggerItemSettings({ keywordRules: initialRules });
      initialTriggerRef.current = {
        ...triggerItem,
        keywordRules: initialRules,
      };
    }
  }, [
    isReplyDm,
    keywordRules.length,
    triggerItem,
    selectedAccountId,
    updateTriggerItemSettings,
  ]);

  // Re-runs whenever the selected account changes (including the initial mount)
  useEffect(() => {
    if (!selectedAccountId || isReplyDm) {
      return;
    }
    let cancelled = false;
    (async () => {
      if (instagramPosts.length === 0) {
        try {
          const response = await (isReplyStory
            ? fetchInstagramStoriesPage(selectedAccountId)
            : (isFacebook ? fetchFacebookPostsPage : fetchInstagramPostsPage)(
                selectedAccountId,
                MORE_POSTS_SIZE,
                undefined,
                isSpecificPostTrigger ? triggerItem.contentId : undefined
              ));
          if (!cancelled) {
            setInstagramPosts(response.posts);
            setInstagramPostsPaging({
              nextCursor: response.paging.nextCursor,
              hasMore: response.paging.hasMore,
            });
            setPostsFetchError(false);
          }
        } catch (e) {
          console.error(e);
          if (!cancelled) {
            setPostsFetchError(true);
          }
        } finally {
          if (!cancelled) {
            setLoadingPosts(false);
          }
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccountId]);

  const selectAccountOnPress = useCallback(
    (newAccountId: string) => {
      if (newAccountId === selectedAccountId) {
        return;
      }
      updateTriggerItemSettings({
        igAccountId: newAccountId,
        ...(isSpecificPostTrigger ? { contentId: "", mediaUrl: "" } : {}),
      });
      setInstagramPosts([]);
      setInstagramPostsPaging({ nextCursor: null, hasMore: false });
      setLoadingPosts(true);
      setPostsFetchError(false);
      setShowSelectPostError(false);
    },
    [selectedAccountId, updateTriggerItemSettings, isSpecificPostTrigger]
  );

  const accountDropdownItems: DropdownMenuItem[] = useMemo(
    () =>
      connectedAccounts.map((account) => ({
        title: isFacebook ? account.username : `@${account.username}`,
        icon: account.id === selectedAccountId ? "ion:checkmark" : undefined,
        key: account.id,
        onPress: () => selectAccountOnPress(account.id),
      })),
    [connectedAccounts, selectedAccountId, selectAccountOnPress, isFacebook]
  );

  useEffect(() => {
    if (instagramPosts.length > 0 && !triggerItem.contentId) {
      const firstPostId = instagramPosts[0].id || "";
      const firstPostImageUrl = instagramPosts[0].imageUrl ?? "";
      updateTriggerItemSettings({
        contentId: firstPostId,
        mediaUrl: firstPostImageUrl,
      });
      initialTriggerRef.current = {
        ...triggerItem,
        contentId: firstPostId,
        mediaUrl: firstPostImageUrl,
      };
    }
  }, [instagramPosts, triggerItem, updateTriggerItemSettings]);

  const loadMoreInstagramPosts = useCallback(async () => {
    if (
      !instagramPostsPaging.hasMore ||
      !instagramPostsPaging.nextCursor ||
      loadingMorePosts ||
      !selectedAccountId
    ) {
      return;
    }
    setLoadingMorePosts(true);
    try {
      const response = await (
        isFacebook ? fetchFacebookPostsPage : fetchInstagramPostsPage
      )(selectedAccountId, MORE_POSTS_SIZE, instagramPostsPaging.nextCursor);
      setInstagramPosts((prev) => {
        const existingIds = new Set(prev.map((p) => p.id));
        return [
          ...prev,
          ...response.posts.filter((p) => !existingIds.has(p.id)),
        ];
      });
      setInstagramPostsPaging({
        nextCursor: response.paging.nextCursor,
        hasMore: response.paging.hasMore,
      });
    } catch (e) {
      console.error(e);
      setPostsFetchError(true);
    }
    setLoadingMorePosts(false);
  }, [
    instagramPostsPaging.hasMore,
    instagramPostsPaging.nextCursor,
    loadingMorePosts,
    selectedAccountId,
    isFacebook,
  ]);

  const showAllPosts = useCallback(async () => {
    setShowAllPostsModal(true);
    await loadMoreInstagramPosts();
  }, [loadMoreInstagramPosts]);

  /** Top strip: first four in feed order unless selection is beyond index 3 — then selected first + next three others in feed order. */
  const instagramPostsPreviewRow = useMemo(() => {
    const posts = instagramPosts;
    const selectedId = triggerItem?.contentId;

    const selectedIndex =
      selectedId != null && selectedId !== ""
        ? posts.findIndex((p) => p.id === selectedId)
        : -1;
    if (selectedIndex === -1 || selectedIndex < INITIAL_WINDOW_SIZE) {
      return posts.slice(0, INITIAL_WINDOW_SIZE);
    }

    const selectedPost = posts[selectedIndex];
    const others = posts
      .filter((p) => p.id !== selectedId)
      .slice(0, INITIAL_WINDOW_SIZE - 1);
    return [selectedPost, ...others];
  }, [instagramPosts, triggerItem?.contentId, INITIAL_WINDOW_SIZE]);

  const selectedPostCaption = useMemo(() => {
    if (!isSpecificPostTrigger) {
      return undefined;
    }
    return (
      instagramPosts.find((post) => post.id === triggerItem.contentId)
        ?.caption ?? undefined
    );
  }, [isSpecificPostTrigger, instagramPosts, triggerItem.contentId]);

  useEffect(() => {
    // Whenever the selected post caption changes, update the post caption in the parent component
    onPostCaptionChange?.(selectedPostCaption);
  }, [selectedPostCaption, onPostCaptionChange]);

  const onKeywordTermChange = useCallback(
    (value: string, index: number) => {
      setShowKeywordError(false);
      updateTriggerItemSettings({
        keywordRules: keywordRules.map((rule) => {
          if (rule.mode !== KeywordRuleMode.INCLUDE) {
            return rule;
          }
          const terms = [...(rule.terms ?? [])];
          while (terms.length <= index) {
            terms.push("");
          }
          terms[index] = value.toLowerCase();
          return { ...rule, terms };
        }),
      });
    },
    [keywordRules, updateTriggerItemSettings]
  );

  const onKeywordTermDelete = useCallback(
    (index: number) => {
      updateTriggerItemSettings({
        keywordRules: keywordRules.map((rule) => {
          if (rule.mode !== KeywordRuleMode.INCLUDE) {
            return rule;
          }
          const terms = [...(rule.terms ?? [])];
          terms.splice(index, 1);
          return {
            ...rule,
            terms: terms.length > 0 ? terms : [""],
          };
        }),
      });
    },
    [keywordRules, updateTriggerItemSettings]
  );

  const onKeywordTermAdd = useCallback(() => {
    const includeIdx = keywordRules.findIndex(
      (r) => r.mode === KeywordRuleMode.INCLUDE
    );
    if (includeIdx === -1) {
      updateTriggerItemSettings({
        keywordRules: [
          ...keywordRules,
          {
            mode: KeywordRuleMode.INCLUDE,
            terms: [""],
          },
        ],
      });
      return;
    }
    updateTriggerItemSettings({
      keywordRules: keywordRules.map((rule, i) =>
        i === includeIdx
          ? {
              ...rule,
              terms: [...(rule.terms ?? []), ""],
            }
          : rule
      ),
    });
  }, [keywordRules, updateTriggerItemSettings]);

  // Called when creating a new trigger within an existing automation
  const saveTriggerOnPress = useCallback(async () => {
    // Check if there is a post or reel selected
    if (!isReplyDm && !triggerItem.contentId) {
      setAlertText({
        header: `You need to select a ${contentLabel.toLowerCase()}.`,
        subHeader: "",
      });
      setShowSelectPostError(true);
      return;
    }

    // Next Post is always selectable - validated here at publish time
    // instead of pre-emptively disabled during account/post selection.
    if (triggerItem.contentId === DMAutomationTriggerContentType.NEXT) {
      const nextTriggerAccountIds = await getInstagramNextTriggerAccountIds(
        accountData.uid,
        triggerItem.id || undefined
      );
      if (nextTriggerAccountIds.includes(selectedAccountId)) {
        setAlertText({
          header: "This account already has a Next Post trigger.",
          subHeader:
            "Only one Next Post trigger can be active per account. Delete the existing one first, or choose a different account.",
        });
        setShowSelectPostError(true);
        return;
      }
    }

    // Next Story: same one-per-account rule as Next Post
    if (triggerItem.contentId === DMAutomationTriggerContentType.NEXT_STORY) {
      const nextStoryAccountIds = await getInstagramNextTriggerAccountIds(
        accountData.uid,
        triggerItem.id || undefined,
        DMAutomationTriggerContentType.NEXT_STORY
      );
      if (nextStoryAccountIds.includes(selectedAccountId)) {
        setAlertText({
          header: "This account already has a Next Story trigger.",
          subHeader:
            "Only one Next Story trigger can be active per account. Delete the existing one first, or choose a different account.",
        });
        setShowSelectPostError(true);
        return;
      }
    }

    const includeKeywordTerms =
      getDMAutomationKeywordIncludeTerms(keywordRules);
    const hasNoKeywordTerms = includeKeywordTerms.every(
      (term) => term.trim() === ""
    );
    if (isReplyDm && hasNoKeywordTerms) {
      setAlertText({
        header: "You need to have at least one keyword.",
        subHeader: "",
      });
      setShowKeywordError(true);
      return;
    }
    if (
      hasKeywordRules &&
      keywordRules.length > 0 &&
      includeKeywordTerms.every((term) => term.trim() === "")
    ) {
      setAlertText({
        header: `You need to have at least one specific keyword, or select any ${isReplyStory ? "reply" : "comment"}.`,
        subHeader: "",
      });
      setShowKeywordError(true);
      return;
    }
    // Check if there are duplicates in the keywords
    const allTerms = keywordRules.flatMap(
      (rule) => rule.terms?.map((t) => t.trim().toLowerCase()) ?? []
    );
    if (new Set(allTerms).size !== allTerms.length) {
      setAlertText({
        header: "You cannot have duplicate keywords.",
        subHeader: "",
      });
      setShowKeywordError(true);
      return;
    }
    // Check if there are duplicates in the public replies (reply triggers have none)
    const allReplies = publicReplies.map((reply) => reply.trim().toLowerCase());
    if (!isReply && new Set(allReplies).size !== allReplies.length) {
      setAlertText({
        header: "You cannot have duplicate public replies.",
        subHeader: "",
      });
      setShowPublicRepliesError(true);
      return;
    }
    // Check if public replies have less than 3 populated options
    if (
      !isReply &&
      hasPublicReplies &&
      publicReplies.length > 0 &&
      publicReplies.filter((reply) => reply.trim() !== "").length < 3
    ) {
      setAlertText({
        header:
          "You need to have at least 3 public replies, or select no reply.",
        subHeader: "",
      });
      setShowPublicRepliesError(true);
      return;
    }
    // Check if any public replies contain censored terms
    if (!isReply && publicReplies.some((reply) => detectedCensored(reply))) {
      setAlertText({
        header: "One of your public replies contains censored terms.",
        subHeader: `Censored terms detected: ${censoredWordsDetected(publicReplies.find((reply) => detectedCensored(reply)) ?? "")}`,
      });
      setShowPublicRepliesError(true);
      return;
    }

    setSavingTrigger(true);
    // Filter out empty terms and empty rules
    const finalKeywordRules =
      isReplyDm || hasKeywordRules
        ? keywordRules
            .map((rule) => ({
              ...rule,
              terms: (rule.terms ?? []).filter((term) => term.trim() !== ""),
            }))
            .filter((rule) => rule.terms.length > 0)
        : [];
    const finalPublicReplies =
      !isReply && hasPublicReplies
        ? publicReplies.filter((reply) => reply.trim() !== "")
        : [];
    const finalTrigger: DMAutomationTrigger = {
      ...triggerItem,
      ...(isReplyDm
        ? { contentId: DMAutomationTriggerContentType.ALL_DM, mediaUrl: "" }
        : {}),
      keywordRules: finalKeywordRules,
      publicReplies: finalPublicReplies,
      igAccountId: triggerItem.igAccountId || selectedAccountId,
    };
    if (nextOnPress) {
      setTriggerItem(finalTrigger);
      nextOnPress();
    } else {
      await API.dmAutomation.writeDmAutomationTrigger({
        trigger: finalTrigger,
      });
      setSavingTrigger(false);
      selectTriggerItem(undefined);
      showNotificationBanner(
        dispatch,
        isEditing ? "Trigger Saved!" : "Trigger Created!",
        NotificationType.AFFIRMATIVE
      );
    }
  }, [
    triggerItem,
    isReplyStory,
    keywordRules,
    hasKeywordRules,
    publicReplies,
    hasPublicReplies,
    nextOnPress,
    setTriggerItem,
    selectTriggerItem,
    dispatch,
    isEditing,
    selectedAccountId,
    accountData.uid,
    contentLabel,
    isReply,
    isReplyDm,
  ]);

  const deleteTrigger = useCallback(async () => {
    const numTriggers = await getNumDMAutomationTriggers(
      accountData.uid,
      dmAutomation.id
    );
    if (numTriggers === 1) {
      setAlertText({
        header: "You must have at least one trigger in an automation.",
        subHeader: "",
      });
      return;
    }
    setShowDeleteModal(true);
  }, [dmAutomation.id, accountData.uid]);

  const dropdownItems: DropdownMenuItem[] = useMemo(() => {
    const items: DropdownMenuItem[] = [
      {
        title: "Delete",
        icon: "ion:trash",
        key: "delete",
        onPress: deleteTrigger,
        isDestructive: true,
      },
    ];
    return items;
  }, [deleteTrigger]);

  const infoIcon = useCallback(
    (url: string) => (
      <Icon
        icon="mdi:information-outline"
        height={14}
        style={{ color: Colors.GRAY1, cursor: "pointer" }}
        onClick={() => window.open(url, "_blank")}
      />
    ),
    []
  );

  const renderAccountDropdown = useMemo(
    () =>
      selectedAccount && connectedAccounts.length > 1 ? (
        <div className="ColumnNormal" style={{ width: "fit-content" }}>
          <DropdownFilter
            items={accountDropdownItems}
            filterState={
              <div className="AlignedRow" style={{ gap: 7 }}>
                <SocialProfilePic account={selectedAccount} size={20} />
                <span className="bodyMedium">
                  {isFacebook
                    ? selectedAccount.username
                    : `@${selectedAccount.username}`}
                </span>
              </div>
            }
            containerStyles={{ padding: "8px 10px" }}
          />
        </div>
      ) : null,
    [
      accountDropdownItems,
      connectedAccounts.length,
      selectedAccount,
      isFacebook,
    ]
  );

  // The keyword inputs + Add Keyword button, shared by the comment/story keywords step and the DM keyword step
  const renderKeywordTermInputs = (
    <>
      {keywordRules.length > 0
        ? automationKeywordRules.map((term, index) => (
            <div key={index} className="AlignedRow" style={{ gap: 10 }}>
              <CustomTextField
                value={term}
                placeholder="Enter keyword here..."
                inputMode="text"
                borderRadius={12}
                onChange={(e) => onKeywordTermChange(e.target.value, index)}
                maxLength={40}
                error={showKeywordError && term.trim() === ""}
              />
              {automationKeywordRules.length > 1 ? (
                <div>
                  <Icon
                    icon={"ion:trash"}
                    height={18}
                    className="TrashButton"
                    onClick={() => onKeywordTermDelete(index)}
                  />
                </div>
              ) : null}
            </div>
          ))
        : null}
      <RectangleButton
        buttonLabel="Add Keyword"
        iconRight={
          <Icon
            icon="ion:add-circle"
            height={18}
            style={{ color: Colors.BLACK }}
          />
        }
        onPress={onKeywordTermAdd}
        altColor={Colors.TRANSPARENT}
        altTextColor={Colors.BLACK}
        containerStyles={{
          border: `0.5px solid ${Colors.GRAY2}`,
          borderRadius: 10,
        }}
      />
    </>
  );

  return (
    <>
      <div
        className="ColumnNormal PreventScroll"
        style={{ backgroundColor: Colors.WHITE, height: "100%" }}
      >
        <div className="AlignedRowSpaced" style={styles.headerRow}>
          <div className="AlignedRow" style={{ gap: 10 }}>
            <BackButton
              onPress={() => {
                if (madeChanges) {
                  setShowDiscardChangesModal(true);
                } else {
                  selectTriggerItem(undefined);
                }
              }}
              iconName={isDraftAutomation ? "ion:close" : "ion:chevron-back"}
            />
            <span className="sectionTitle">
              {isDraftAutomation
                ? "New Automation"
                : isEditing
                  ? "Edit Trigger"
                  : "New Trigger"}
            </span>
          </div>
          <div className="AlignedRow" style={{ gap: 10 }}>
            {isEditing ? (
              <DropdownMenu
                dropdownItems={dropdownItems}
                trigger={["click"]}
                placement="bottomRight"
                altWidth={204}
              >
                <Icon
                  icon={"mdi:dots-horizontal"}
                  height={22}
                  style={{ color: Colors.BLACK }}
                />
              </DropdownMenu>
            ) : null}
            <RectangleButton
              buttonLabel={
                isDraftAutomation ? "Next" : isEditing ? "Save" : "Create"
              }
              onPress={saveTriggerOnPress}
              loading={savingTrigger}
              altColor={Colors.BLACK}
              altTextColor={Colors.WHITE}
              altBorderRadius={100}
              containerStyles={{ padding: "10px 14px" }}
            />
          </div>
        </div>
        <HorizontalDivider altMargin={0} />
        <div
          className="ColumnNormal AllowScroll"
          style={{ padding: 14, paddingBottom: 74, gap: 24 }}
        >
          {!isReplyDm ? (
            <div
              className="ColumnNormal onboarding-instagram-dm-selectpost-target"
              style={{ gap: 14 }}
            >
              <span className="bodyMedium">Step 1 of {totalSteps}</span>
              <div
                className="ColumnNormal"
                style={{
                  ...styles.stepContainer,
                  border: showSelectPostError
                    ? `1.5px solid ${Colors.RED3}`
                    : `0.5px solid ${Colors.GRAY2}`,
                }}
              >
                <div className="ColumnNormal" style={{ gap: 4 }}>
                  <span className="bodyMedium">Select {contentLabel}</span>
                  <div className="AlignedRow" style={{ gap: 4 }}>
                    <span className="smallBodySubtext">
                      Which {contentLabel} do you want to use for this
                      automation?
                    </span>
                    {!isFacebook && !isReplyStory
                      ? infoIcon(
                          MARKIT_DOCS +
                            "/instagramautomations#automation-priority"
                        )
                      : null}
                  </div>
                </div>
                <SelectableOptionCard
                  title={`All ${contentLabelPlural}`}
                  subtext={`Any ${contentLabel} will trigger this automation`}
                  isSelected={
                    triggerItem.contentId ===
                    (isReplyStory
                      ? DMAutomationTriggerContentType.ALL_STORY
                      : DMAutomationTriggerContentType.ALL)
                  }
                  onSelect={() => {
                    setShowSelectPostError(false);
                    updateTriggerItemSettings({
                      contentId: isReplyStory
                        ? DMAutomationTriggerContentType.ALL_STORY
                        : DMAutomationTriggerContentType.ALL,
                      mediaUrl: "",
                    });
                  }}
                >
                  {renderAccountDropdown}
                </SelectableOptionCard>
                <SelectableOptionCard
                  title={`Next ${contentLabel}`}
                  subtext={`The first ${isReplyStory ? "reply" : "comment"} on the next ${contentLabel} published will trigger this automation`}
                  isSelected={
                    triggerItem.contentId ===
                    (isReplyStory
                      ? DMAutomationTriggerContentType.NEXT_STORY
                      : DMAutomationTriggerContentType.NEXT)
                  }
                  onSelect={() => {
                    setShowSelectPostError(false);
                    updateTriggerItemSettings({
                      contentId: isReplyStory
                        ? DMAutomationTriggerContentType.NEXT_STORY
                        : DMAutomationTriggerContentType.NEXT,
                      mediaUrl: "",
                    });
                  }}
                >
                  {renderAccountDropdown}
                </SelectableOptionCard>
                <SelectableOptionCard
                  title={`Specific ${contentLabel}`}
                  subtext={`The selected ${contentLabel} will trigger this automation`}
                  isSelected={isSpecificPostTrigger}
                  onSelect={() => {
                    setShowSelectPostError(false);
                    updateTriggerItemSettings({
                      contentId:
                        instagramPosts.length > 0 ? instagramPosts[0].id : "",
                      mediaUrl:
                        instagramPosts.length > 0
                          ? (instagramPosts[0].imageUrl ?? "")
                          : "",
                    });
                  }}
                  disabled={loadingPosts}
                >
                  <div className="ColumnNormal" style={{ gap: 14 }}>
                    {renderAccountDropdown}
                    <div className="AlignedRow" style={{ gap: 10 }}>
                      {loadingPosts ? (
                        [
                          Array.from({ length: INITIAL_WINDOW_SIZE + 1 }).map(
                            (_, index) => (
                              <Skeleton
                                key={index}
                                width={IMAGE_WIDTH}
                                height={IMAGE_HEIGHT}
                                borderRadius={14}
                              />
                            )
                          ),
                        ]
                      ) : postsFetchError || selectedAccountErrored ? (
                        <div className="ColumnNormal" style={{ gap: 10 }}>
                          <span className="bodySmallMedium">
                            We couldn&apos;t reach your{" "}
                            {isFacebook ? "Facebook Page" : "Instagram account"}
                            . Reconnect it to keep using automations.
                          </span>
                          <RectangleButton
                            buttonLabel="Reconnect"
                            onPress={() => setShowReconnectModal(true)}
                            altColor={Colors.BLACK}
                            altTextColor={Colors.WHITE}
                            altPaddingHorz={14}
                            altPaddingVert={10}
                            containerStyles={{ width: "fit-content" }}
                          />
                        </div>
                      ) : instagramPosts.length === 0 ? (
                        <span className="bodySmallMedium">
                          {isReplyStory
                            ? "No active stories — stories are only available for 24 hours."
                            : isFacebook
                              ? "No posts found. Publish a post on your Page."
                              : "No posts or reels found. Publish media on your account."}
                        </span>
                      ) : (
                        <div
                          className="AlignedRow"
                          style={{ gap: 10, flexWrap: "wrap" }}
                        >
                          {instagramPostsPreviewRow.map((postItem) => (
                            <div
                              key={postItem.id}
                              onClick={() => {
                                setShowSelectPostError(false);
                                updateTriggerItemSettings({
                                  contentId: postItem.id,
                                  mediaUrl: postItem.imageUrl ?? "",
                                });
                              }}
                              style={{
                                width: IMAGE_WIDTH,
                                height: IMAGE_HEIGHT,
                                borderRadius: 14,
                                boxShadow: `0 0 0 ${triggerItem.contentId === postItem.id ? `3px ${Colors.BLUE5}` : `1px ${Colors.GRAY2}`}`,
                                backgroundColor: postItem.imageUrl
                                  ? undefined
                                  : Colors.WHITE,
                                backgroundImage: postItem.imageUrl
                                  ? `url(${postItem.imageUrl})`
                                  : undefined,
                                backgroundSize: "cover",
                                backgroundPosition: "center",
                                flexShrink: 0,
                                position: "relative",
                                cursor: "pointer",
                                overflow: "hidden",
                              }}
                            >
                              {!postItem.imageUrl ? (
                                <div
                                  className="Centering"
                                  style={{ width: "100%", height: "100%" }}
                                >
                                  {isFacebook ? (
                                    <span
                                      className="minyBodySubtext TwoLineText"
                                      style={{ textAlign: "center" }}
                                    >
                                      {postItem.caption || "No caption"}
                                    </span>
                                  ) : (
                                    <Icon
                                      icon="mdi:image-off-outline"
                                      height={26}
                                      style={{ color: Colors.BLACK }}
                                    />
                                  )}
                                </div>
                              ) : null}
                            </div>
                          ))}
                          <div
                            className="Centering"
                            style={{ width: IMAGE_WIDTH, height: IMAGE_HEIGHT }}
                          >
                            <TextHoverButton
                              text="See All"
                              icon={
                                <Icon icon="ion:chevron-right" height={14} />
                              }
                              iconPosition="right"
                              onPress={showAllPosts}
                              textStyles={{ fontSize: 12, color: Colors.BLACK }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </SelectableOptionCard>
              </div>
            </div>
          ) : null}

          {isReplyDm ? (
            <div
              className="ColumnNormal onboarding-instagram-dm-trigger-target"
              style={{ gap: 14 }}
            >
              <StandardBorderedContainer
                containerStyles={styles.anyKeywordNoticeContainer}
              >
                <div className="ColumnNormal">
                  <span className="bodySmallRegular">
                    Make sure to use unique keywords that would not come up in
                    normal conversation. Avoid common words to ensure this
                    automation is only triggered intentionally.
                  </span>
                </div>
              </StandardBorderedContainer>
              <div className="ColumnNormal" style={styles.stepContainer}>
                <div className="ColumnNormal" style={{ gap: 4 }}>
                  <span className="bodyMedium">When someone DMs you with</span>
                </div>
                <div className="ColumnNormal" style={{ gap: 10 }}>
                  {renderAccountDropdown}
                  {renderKeywordTermInputs}
                </div>
              </div>
            </div>
          ) : null}

          {!isReplyDm ? (
            <div
              className="ColumnNormal onboarding-instagram-dm-trigger-target"
              style={{ gap: 14 }}
            >
              <span className="bodyMedium">Step 2 of {totalSteps}</span>
              <div className="ColumnNormal" style={styles.stepContainer}>
                <div className="ColumnNormal" style={{ gap: 4 }}>
                  <span className="bodyMedium">Define your trigger</span>
                  <div className="AlignedRow" style={{ gap: 4 }}>
                    <span className="smallBodySubtext">
                      Should any {isReplyStory ? "reply" : "comment"} trigger
                      the automation, or only specific keywords?
                    </span>
                    {!isFacebook
                      ? infoIcon(MARKIT_DOCS + "/instagramautomations#keywords")
                      : null}
                  </div>
                </div>
                <div className="ColumnNormal" style={{ gap: 10 }}>
                  <SelectableOptionCard
                    title={isReplyStory ? "Any Reply" : "Any Comment"}
                    subtext={
                      isReplyStory
                        ? "When a user replies anything to your Story"
                        : "When a user comments anything on your Post or Reel"
                    }
                    isSelected={!hasKeywordRules}
                    onSelect={() => {
                      setShowKeywordError(false);
                      setHasKeywordRules(false);
                    }}
                  >
                    <StandardBorderedContainer
                      containerStyles={styles.anyKeywordNoticeContainer}
                    >
                      <div className="ColumnNormal" style={{ gap: 4 }}>
                        <span className="bodySmallRegular">
                          Use with caution. It may increase spam reports unless
                          followers are instructed what to{" "}
                          {isReplyStory ? "reply" : "comment"}. Specific
                          keywords are recommended.
                        </span>
                      </div>
                    </StandardBorderedContainer>
                  </SelectableOptionCard>
                  <SelectableOptionCard
                    title={
                      isReplyStory
                        ? "Only replies with specific keywords"
                        : "Only comments with specific keywords"
                    }
                    subtext={
                      isReplyStory
                        ? "When a user replies with something containing one of these terms"
                        : "When a user comments something containing one of these terms"
                    }
                    isSelected={hasKeywordRules}
                    onSelect={() => {
                      setShowKeywordError(false);
                      setHasKeywordRules(true);
                    }}
                  >
                    <div className="ColumnNormal" style={{ gap: 10 }}>
                      {renderKeywordTermInputs}
                    </div>
                  </SelectableOptionCard>
                </div>
              </div>
            </div>
          ) : null}

          {!isReply ? (
            <div
              className="ColumnNormal onboarding-instagram-dm-publicreply-target"
              style={{ gap: 14 }}
            >
              <span className="bodyMedium">Step 3 of {totalSteps}</span>
              <div className="ColumnNormal" style={styles.stepContainer}>
                <div className="ColumnNormal" style={{ gap: 4 }}>
                  <span className="bodyMedium">
                    Set up public reply in the feed
                  </span>
                  <div className="AlignedRow" style={{ gap: 4 }}>
                    <span className="smallBodySubtext">
                      Would you like to reply to the comment within the Post or
                      Reel?
                    </span>
                    {!isFacebook
                      ? infoIcon(
                          MARKIT_DOCS + "/instagramautomations#comment-replies"
                        )
                      : null}
                  </div>
                </div>
                <div className="ColumnNormal" style={{ gap: 10 }}>
                  <SelectableOptionCard
                    title="No Reply"
                    subtext="Don't send a reply in response to the comment"
                    isSelected={!hasPublicReplies}
                    onSelect={() => {
                      setHasPublicReplies(false);
                      setShowPublicRepliesError(false);
                    }}
                  />
                  <SelectableOptionCard
                    title="Yes, random multiple reply options"
                    subtext="One of the replies will be sent in response to the comment"
                    isSelected={hasPublicReplies}
                    onSelect={() => {
                      setHasPublicReplies(true);
                      if (publicReplies.length === 0) {
                        updateTriggerItemSettings({
                          publicReplies: selectRandomDMAutomationPublicReply(
                            3,
                            dmAutomation.platform
                          ),
                        });
                      }
                    }}
                  >
                    <div className="ColumnNormal" style={{ gap: 10 }}>
                      {publicReplies.length > 0
                        ? publicReplies.map((reply, index) => (
                            <div
                              key={index}
                              className="AlignedRow"
                              style={{ gap: 10 }}
                            >
                              <CustomTextField
                                value={reply}
                                placeholder="Enter reply here..."
                                inputMode="text"
                                borderRadius={12}
                                onChange={(e) => {
                                  setShowPublicRepliesError(false);
                                  updateTriggerItemSettings({
                                    publicReplies: publicReplies.map((r, i) =>
                                      i === index ? e.target.value : r
                                    ),
                                  });
                                }}
                                maxLength={100}
                                error={
                                  showPublicRepliesError &&
                                  (reply.trim() === "" ||
                                    detectedCensored(reply))
                                }
                              />
                              {publicReplies.length > 3 ? (
                                <div>
                                  <Icon
                                    icon={"ion:trash"}
                                    height={18}
                                    className="TrashButton"
                                    onClick={() =>
                                      updateTriggerItemSettings({
                                        publicReplies: publicReplies.filter(
                                          (_, i) => i !== index
                                        ),
                                      })
                                    }
                                  />
                                </div>
                              ) : null}
                            </div>
                          ))
                        : null}
                      <RectangleButton
                        buttonLabel="Add Reply"
                        iconRight={
                          <Icon
                            icon="ion:add-circle"
                            height={18}
                            style={{ color: Colors.BLACK }}
                          />
                        }
                        onPress={() =>
                          updateTriggerItemSettings({
                            publicReplies: [...publicReplies, ""],
                          })
                        }
                        altColor={Colors.TRANSPARENT}
                        altTextColor={Colors.BLACK}
                        containerStyles={{
                          border: `1px solid ${Colors.GRAY2}`,
                          borderRadius: 10,
                          height: 42,
                        }}
                      />
                    </div>
                  </SelectableOptionCard>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
      {!isFacebook &&
      isDraftAutomation &&
      hasSocialAccountConnection(instagramAccounts) ? (
        isReplyDm ? (
          <OnboardingTourStep
            key="instagram-dm-tour-step-2-dm"
            flowType={OnboardingFlowType.INSTAGRAM_DM}
            target=".onboarding-instagram-dm-trigger-target"
            content={
              <>
                Choose the keywords that trigger this automation when someone
                DMs you. Click &quot;Next&quot; when done.
              </>
            }
            stepNumber={2}
            totalSteps={tourTotalSteps}
            placement="right"
            mountDelayMs={300}
          />
        ) : triggerTourStep === 1 ? (
          <OnboardingTourStep
            key="instagram-dm-tour-step-2"
            flowType={OnboardingFlowType.INSTAGRAM_DM}
            target=".onboarding-instagram-dm-selectpost-target"
            content={
              isReplyStory
                ? "Choose which story triggers this automation: all, next, or a specific story. A specific story stops triggering after 24 hours."
                : "Choose what post you want your automation to be triggered on. You can select all posts, your next post, or a specific post or reel."
            }
            stepNumber={2}
            totalSteps={tourTotalSteps}
            placement="right"
            onStepComplete={() => setTriggerTourStep(2)}
            mountDelayMs={300}
          />
        ) : triggerTourStep === 2 || isReplyStory ? (
          <OnboardingTourStep
            key="instagram-dm-tour-step-3"
            flowType={OnboardingFlowType.INSTAGRAM_DM}
            target=".onboarding-instagram-dm-trigger-target"
            content={
              isReplyStory ? (
                <>
                  Choose whether any reply triggers the automation, or only
                  replies with specific keywords. Click &quot;Next&quot; when
                  done.
                </>
              ) : (
                "Next, choose keywords that users comment to trigger the automation. Pick keywords that are specific enough so you target users that are interested in your offerings."
              )
            }
            stepNumber={3}
            totalSteps={tourTotalSteps}
            placement="right"
            onStepComplete={
              isReplyStory ? undefined : () => setTriggerTourStep(3)
            }
          />
        ) : (
          <OnboardingTourStep
            key="instagram-dm-tour-step-4"
            flowType={OnboardingFlowType.INSTAGRAM_DM}
            target=".onboarding-instagram-dm-publicreply-target"
            content={
              <>
                You can customize the public replies to the comments to make
                them sound more like you.
                <br />
                <br />
                Click &quot;Next&quot; to continue after filling in the
                automation details.
              </>
            }
            stepNumber={4}
            totalSteps={tourTotalSteps}
            placement="right"
          />
        )
      ) : null}
      {showAllPostsModal ? (
        <PopupModalContainer
          altPadding={0}
          valueComp={
            <div className="ColumnNormal" style={{ gap: 14 }}>
              <div className="AlignedRowSpaced" style={{ paddingInline: 14 }}>
                <span className="sectionTitle">Select {contentLabel}</span>
                <BackButton
                  onPress={() => setShowAllPostsModal(false)}
                  iconName="ion:close"
                />
              </div>
              <HorizontalDivider altMargin={0} />

              <div
                className="photoGallerySample"
                style={{ paddingInline: 14, gap: 10 }}
              >
                {instagramPosts.map((post) => (
                  <div
                    key={post.id}
                    className="ColumnNormal"
                    style={{
                      gap: 7,
                      borderRadius: 14,
                      border:
                        post.id === triggerItem?.contentId
                          ? `2px solid ${Colors.BLUE5}`
                          : `1px solid ${Colors.GRAY2}`,
                      width: 142,
                    }}
                  >
                    {post.imageUrl ? (
                      <img
                        src={post.imageUrl}
                        style={{
                          width: 142,
                          height: 142,
                          borderTopRightRadius: 14,
                          borderTopLeftRadius: 14,
                          borderBottomRightRadius: 0,
                          borderBottomLeftRadius: 0,
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          if (post.id === triggerItem?.contentId) {
                            return;
                          }
                          updateTriggerItemSettings({
                            contentId: post.id,
                            mediaUrl: post.imageUrl ?? "",
                          });
                          setShowAllPostsModal(false);
                        }}
                      />
                    ) : (
                      <div
                        className="Centering"
                        style={{
                          width: 142,
                          height: 142,
                          borderBottom: `0.5px solid ${Colors.GRAY2}`,
                        }}
                      >
                        <span
                          className="smallBodySubtext TwoLineText"
                          style={{ textAlign: "center" }}
                        >
                          {post.caption || "No caption"}
                        </span>
                      </div>
                    )}
                    <div
                      className="ColumnNormal"
                      style={{ gap: 4, paddingInline: 7, paddingBottom: 7 }}
                    >
                      {!isReplyStory ? (
                        <span className="bodyMedium OneLineText">
                          {post.caption || "No caption"}
                        </span>
                      ) : null}
                      {post.permalink ? (
                        <a
                          href={post.permalink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="bodySmallMedium"
                          style={{ color: Colors.BLUE5, cursor: "pointer" }}
                        >
                          {isFacebook
                            ? "View on Facebook"
                            : "View on Instagram"}
                        </a>
                      ) : null}
                      <span className="smallBodySubtext">
                        {GetDateWithTime(post.timestamp || "", true)}
                      </span>
                    </div>
                  </div>
                ))}
                {loadingMorePosts ? (
                  <div
                    className="Centering"
                    style={{ flexBasis: "100%", paddingBlock: 14 }}
                  >
                    <CircularProgress
                      style={{ color: Colors.GRAY1 }}
                      size={24}
                    />
                  </div>
                ) : instagramPostsPaging.hasMore ? (
                  <div
                    className="Centering"
                    style={{ flexBasis: "100%", paddingBottom: 4 }}
                  >
                    <RectangleButton
                      buttonLabel="Load More"
                      iconRight={<Icon icon="ion:chevron-right" height={14} />}
                      onPress={loadMoreInstagramPosts}
                      altColor={Colors.WHITE1}
                      altTextColor={Colors.BLACK}
                      altPaddingHorz={14}
                      containerStyles={{
                        border: `1px solid ${Colors.GRAY2}`,
                        borderRadius: 14,
                        width: "fit-content",
                      }}
                    />
                  </div>
                ) : null}
              </div>
            </div>
          }
          noExit
          noDivider
          closeOnOutsidePress
          closeModal={() => setShowAllPostsModal(false)}
          hideModal={!showAllPostsModal}
          modalContentWidth={PopupModalContainerWidth.WIDE}
        />
      ) : null}
      <AlertContainer
        headerComp={alertText.header}
        subHeaderComp={
          alertText.subHeader !== "" ? alertText.subHeader : undefined
        }
        closeModal={() => setAlertText({ header: "", subHeader: "" })}
        hideModal={alertText.header === ""}
      />
      <ConfirmDiscardChangesModal
        isVisible={showDiscardChangesModal}
        setIsVisible={setShowDiscardChangesModal}
        deleteOnPress={() => selectTriggerItem(undefined)}
      />
      {isEditing ? (
        <DMAutomationConfirmDeleteModal
          id={triggerItem.id}
          dmAutomationId={dmAutomation.id}
          type="Trigger"
          showDeleteModal={showDeleteModal}
          setShowDeleteModal={setShowDeleteModal}
          handleAfterDelete={deleteTriggerOnPress}
        />
      ) : null}
      {isFacebook ? (
        <ConnectFacebookModal
          showModal={showReconnectModal}
          setShowModal={setShowReconnectModal}
        />
      ) : (
        <ConnectInstagramModal
          showModal={showReconnectModal}
          setShowModal={setShowReconnectModal}
        />
      )}
    </>
  );
};

export default DMAutomationCreateTrigger;
